import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Info as InfoIcon, Search as SearchIcon, Notifications as NotificationsIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface PushSubscription {
  keys: {
    auth: string;
    p256dh: string;
  };
  endpoint: string;
}

interface Location {
  id: number;
  name: string;
  address: string;
}

interface Client {
  id: string;
  lastActive: string;
  pushSubscription: PushSubscription | null;
  deviceType: string;
  browser: string;
  browserVersion: string;
  createdAt: string;
  updatedAt: string;
  favoritesCount: number;
  favorites: Location[];
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number | null;
  prevPage: number | null;
}

interface OrderingInfo {
  orderBy: string;
  orderDirection: 'ASC' | 'DESC';
}

interface PaginatedResponse {
  data: Client[];
  pagination: PaginationInfo;
  ordering: OrderingInfo;
}

type OrderByField = 'id' | 'lastActive' | 'createdAt' | 'updatedAt' | 'deviceType' | 'browser' | 'operatingSystem';
type OrderDirection = 'ASC' | 'DESC';

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

const ClientsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [orderBy, setOrderBy] = useState<OrderByField>('createdAt');
  const [orderDirection, setOrderDirection] = useState<OrderDirection>('DESC');
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [favoritesDialogOpen, setFavoritesDialogOpen] = useState(false);
  const [selectedClientFavorites, setSelectedClientFavorites] = useState<Location[]>([]);
  const [selectedClientName, setSelectedClientName] = useState<string>('');
  
  // Push notification state
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);

  // Debounce the filter to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(filter);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [filter]);

  // Memoize the query function to prevent unnecessary re-renders
  const queryFn = useCallback(async () => {
    const params: any = {
      page: page + 1, // API expects 1-based pagination
      limit: rowsPerPage,
      orderBy,
      orderDirection
    };

    // Add server-side filtering by ID if filter is provided
    if (debouncedFilter) {
      params.id = debouncedFilter;
    }

    const response = await axios.get(`${process.env.REACT_APP_API_URL}/clients/paginated`, {
      params
    });
    console.log('API Response:', response.data); // Debug log
    return response.data as PaginatedResponse;
  }, [page, rowsPerPage, debouncedFilter, orderBy, orderDirection]);

  const { data: clientsData, isLoading, error } = useQuery<PaginatedResponse>({
    queryKey: ['clients', page, rowsPerPage, debouncedFilter, orderBy, orderDirection],
    queryFn,
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Extract data from the API response structure
  const clients: Client[] = clientsData?.data ?? [];
  
  // Use server-provided pagination
  const pagination = clientsData?.pagination || {
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  };

  // Debug logging
  console.log('clientsData:', clientsData);
  console.log('clients array:', clients);
  console.log('pagination:', pagination);

  const sendPushNotificationMutation = useMutation({
    mutationFn: async ({ clientId, title, body }: { clientId: string; title: string; body: string }) => {
      const payload = {
        title,
        body,
        data: {
          clientId,
          timestamp: new Date().toISOString()
        },
        clientIds: [clientId],
        options: {
          TTL: 24 * 60 * 60, // 24 hours in seconds
          urgency: 'high',
          topic: 'test-notification'
        }
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/push/notify`, payload);
    },
    onSuccess: () => {
      setNotification({
        open: true,
        message: 'Push notification sent successfully',
        severity: 'success',
      });
      setPushDialogOpen(false);
      setSelectedClient(null);
      setPushTitle('');
      setPushBody('');
    },
    onError: (err) => {
      console.error('Error sending push notification:', err);
      setNotification({
        open: true,
        message: 'Failed to send push notification',
        severity: 'error',
      });
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: async (clientId: string) => {
      await axios.delete(`${process.env.REACT_APP_API_URL}/clients/${clientId}`);
    },
    onSuccess: () => {
      setNotification({
        open: true,
        message: 'Client deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      // Invalidate and refetch clients data
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (err) => {
      console.error('Error deleting client:', err);
      setNotification({
        open: true,
        message: 'Failed to delete client',
        severity: 'error',
      });
    },
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0); // Reset page when filter changes
  }, []);

  const handleOrderByChange = (event: SelectChangeEvent<OrderByField>) => {
    setOrderBy(event.target.value as OrderByField);
    setPage(0); // Reset page when ordering changes
  };

  const handleOrderDirectionChange = (event: SelectChangeEvent<OrderDirection>) => {
    setOrderDirection(event.target.value as OrderDirection);
    setPage(0); // Reset page when ordering changes
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleViewFavorites = (client: Client) => {
    setSelectedClientFavorites(client.favorites || []);
    setSelectedClientName(`Client ${client.id}`);
    setFavoritesDialogOpen(true);
  };

  const handleCloseFavoritesDialog = () => {
    setFavoritesDialogOpen(false);
    setSelectedClientFavorites([]);
    setSelectedClientName('');
  };

  const handlePushClick = (client: Client) => {
    setSelectedClient(client);
    setPushTitle(`Benachrichtigung`);
    setPushBody('');
    setPushDialogOpen(true);
  };

  const handlePushSend = () => {
    if (selectedClient?.id && pushTitle.trim() && pushBody.trim()) {
      sendPushNotificationMutation.mutate({
        clientId: selectedClient.id,
        title: pushTitle.trim(),
        body: pushBody.trim(),
      });
    }
  };

  const handlePushCancel = () => {
    setPushDialogOpen(false);
    setSelectedClient(null);
    setPushTitle('');
    setPushBody('');
  };

  const handleDeleteClick = (client: Client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (clientToDelete?.id) {
      deleteClientMutation.mutate(clientToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          {axios.isAxiosError(error) && error.response?.status === 401
            ? t('clients.authRequired')
            : t('clients.errorLoading')
          }
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('clients.title')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', mt: 3 }}>
        <TextField
          ref={searchInputRef}
          label={t('clients.searchClients')}
          value={filter}
          onChange={handleFilterChange}
          placeholder={t('clients.searchPlaceholder')}
          variant="outlined"
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('clients.orderBy')}</InputLabel>
          <Select
            value={orderBy}
            label={t('clients.orderBy')}
            onChange={handleOrderByChange}
          >
            <MenuItem value="createdAt">{t('clients.createdDate')}</MenuItem>
            <MenuItem value="updatedAt">{t('clients.updatedDate')}</MenuItem>
            <MenuItem value="lastActive">{t('clients.lastActive')}</MenuItem>
            <MenuItem value="id">ID</MenuItem>
            <MenuItem value="deviceType">{t('clients.deviceType')}</MenuItem>
            <MenuItem value="browser">{t('clients.browser')}</MenuItem>
            <MenuItem value="operatingSystem">{t('clients.operatingSystem')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('clients.direction')}</InputLabel>
          <Select
            value={orderDirection}
            label={t('clients.direction')}
            onChange={handleOrderDirectionChange}
          >
            <MenuItem value="ASC">{t('clients.ascending')}</MenuItem>
            <MenuItem value="DESC">{t('clients.descending')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>{t('clients.pushSubscription')}</TableCell>
              <TableCell>{t('clients.deviceType')}</TableCell>
              <TableCell>{t('clients.browser')}</TableCell>
              <TableCell>{t('clients.browserVersion')}</TableCell>
              <TableCell>{t('clients.favorites')}</TableCell>
              <TableCell>{t('clients.createdDate')}</TableCell>
              <TableCell>{t('clients.lastActive')}</TableCell>
              <TableCell>{t('clients.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients?.map((client) => (
              <TableRow key={client.id}>
                <TableCell>{client.id}</TableCell>
                <TableCell>
                  {client.pushSubscription ? (
                    <Chip 
                      label={t('clients.active')} 
                      color="success" 
                      size="small"
                      title={client.pushSubscription.endpoint}
                    />
                  ) : (
                    <Chip 
                      label={t('clients.inactive')} 
                      color="default" 
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>{client.deviceType || t('clients.unknown')}</TableCell>
                <TableCell>{client.browser || t('clients.unknown')}</TableCell>
                <TableCell>{client.browserVersion || t('clients.unknown')}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {client.favoritesCount || 0}
                    </Typography>
                    {client.favoritesCount > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => handleViewFavorites(client)}
                        title={t('clients.viewFavoriteLocations')}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{formatDate(client.createdAt)}</TableCell>
                <TableCell>{formatDate(client.lastActive)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      onClick={() => handlePushClick(client)}
                      size="small"
                      color="primary"
                      disabled={!client.pushSubscription || sendPushNotificationMutation.isPending}
                      title={t('clients.sendPushNotification')}
                    >
                      <NotificationsIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteClick(client)}
                      size="small"
                      color="error"
                      disabled={deleteClientMutation.isPending}
                      title={t('clients.deleteClient')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Favorites Dialog */}
      <Dialog
        open={favoritesDialogOpen}
        onClose={handleCloseFavoritesDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('clients.favoriteLocations')}<br/>({selectedClientName})
        </DialogTitle>
        <DialogContent>
          {selectedClientFavorites.length > 0 ? (
            <List>
              {selectedClientFavorites.map((location) => (
                <ListItem key={location.id} divider>
                  <ListItemText
                    primary={location.name}
                    secondary={location.address}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('clients.noFavoriteLocations')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFavoritesDialog}>{t('clients.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Push Notification Dialog */}
      <Dialog
        open={pushDialogOpen}
        onClose={handlePushCancel}
        maxWidth="sm"
        fullWidth
        aria-labelledby="push-dialog-title"
      >
        <DialogTitle id="push-dialog-title">
          Send Push Notification
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This notification will be sent to client {selectedClient?.id}.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Notification Title"
            fullWidth
            variant="outlined"
            value={pushTitle}
            onChange={(e) => setPushTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notification Message"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={pushBody}
            onChange={(e) => setPushBody(e.target.value)}
            placeholder="Enter your notification message here..."
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePushCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handlePushSend} 
            color="primary" 
            variant="contained"
            disabled={!pushTitle.trim() || !pushBody.trim() || sendPushNotificationMutation.isPending}
            startIcon={<NotificationsIcon />}
          >
            {sendPushNotificationMutation.isPending ? 'Sending...' : 'Send Push'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete Client
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete client <strong>{clientToDelete?.id}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The client and all associated data (favorites, push subscriptions, etc.) will be permanently removed.
          </Typography>
          {clientToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Client Details:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Device: {clientToDelete.deviceType || 'Unknown'} ({clientToDelete.browser || 'Unknown'})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Favorites: {clientToDelete.favoritesCount || 0} locations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Push Subscription: {clientToDelete.pushSubscription ? 'Active' : 'Inactive'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Created: {formatDate(clientToDelete.createdAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteClientMutation.isPending}
            startIcon={<DeleteIcon />}
          >
            {deleteClientMutation.isPending ? 'Deleting...' : 'Delete Client'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientsPage; 